import { useState } from "react";
import { Phone, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/site";

export default function CallbackWidget() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Inserisci nome e telefono");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/callbacks", form);
      setDone(true);
      toast.success("Richiamo richiesto. Ti chiamerò in giornata.");
    } catch {
      toast.error("Errore durante l'invio");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ name: "", phone: "", preferred_time: "" });
    setConsent(false);
    setDone(false);
    setOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="callback-fab"
        className="fixed bottom-6 left-6 z-40 group inline-flex items-center gap-2 bg-[#3D3B3A] text-white hover:bg-[#7E9C7B] rounded-full pl-4 pr-5 py-3 shadow-[0_12px_30px_rgba(0,0,0,0.18)] transition-all duration-300"
      >
        <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-[#9DB39A]">
          <Phone size={14} strokeWidth={2} />
          <span className="absolute inset-0 rounded-full bg-[#9DB39A] animate-ping opacity-40" />
        </span>
        <span className="text-xs sm:text-sm tracking-wide">Richiamami in giornata</span>
      </button>

      {open && (
        <div
          data-testid="callback-modal"
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-[#3D3B3A]/40 backdrop-blur-sm p-4"
          onClick={reset}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#F6F8F3] rounded-3xl p-8 border border-[#CFD9C5] relative"
          >
            <button
              onClick={reset}
              data-testid="callback-close"
              className="absolute top-5 right-5 text-[#73706E] hover:text-[#3D3B3A]"
              aria-label="Chiudi"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            {!done ? (
              <form onSubmit={submit}>
                <span className="eyebrow">Richiamami</span>
                <h3 className="font-serif-elegant text-3xl text-[#3D3B3A] mt-3 leading-tight">
                  Ti chiamo io,<br />
                  <span className="italic text-[#6B8568]">in giornata</span>.
                </h3>
                <p className="mt-3 text-sm text-[#73706E] leading-relaxed">
                  Lasciami il tuo numero: ti contatterò personalmente entro le 24 ore
                  per fissare insieme un primo colloquio.
                </p>

                <div className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="cbn" className="text-xs text-[#73706E]">Nome *</Label>
                    <Input
                      id="cbn"
                      data-testid="callback-input-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-2 bg-transparent border-0 border-b border-[#CFD9C5] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cbp" className="text-xs text-[#73706E]">Telefono *</Label>
                    <Input
                      id="cbp"
                      data-testid="callback-input-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-2 bg-transparent border-0 border-b border-[#CFD9C5] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cbt" className="text-xs text-[#73706E]">Quando preferisci essere richiamata/o?</Label>
                    <Input
                      id="cbt"
                      data-testid="callback-input-time"
                      placeholder="Es. dopo le 18:00"
                      value={form.preferred_time}
                      onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                      className="mt-2 bg-transparent border-0 border-b border-[#CFD9C5] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#9DB39A]"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer mt-6">
                  <input
                    type="checkbox"
                    data-testid="callback-consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 accent-[#9DB39A]"
                    required
                  />
                  <span className="text-xs text-[#73706E] leading-relaxed">
                    Acconsento al trattamento dei miei dati per essere ricontattato/a (
                    <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#7E9C7B] underline">
                      Privacy Policy
                    </a>
                    ). *
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="callback-submit"
                  className="mt-8 w-full bg-[#9DB39A] text-white hover:bg-[#7E9C7B] disabled:opacity-60 rounded-full py-3.5 text-sm tracking-wide transition-all duration-300"
                >
                  {submitting ? "Invio in corso…" : "Richiedi richiamo"}
                </button>
                <p className="mt-3 text-[11px] text-center text-[#73706E]">
                  I tuoi dati sono trattati con riservatezza nel rispetto del segreto professionale.
                </p>
              </form>
            ) : (
              <div className="text-center py-4" data-testid="callback-success">
                <div className="mx-auto w-14 h-14 rounded-full bg-[#9DB39A]/20 flex items-center justify-center">
                  <Phone size={20} strokeWidth={1.5} className="text-[#7E9C7B]" />
                </div>
                <h3 className="font-serif-elegant text-2xl text-[#3D3B3A] mt-5">Richiesta ricevuta.</h3>
                <p className="mt-3 text-sm text-[#73706E] leading-relaxed">
                  Ti contatterò personalmente in giornata. Grazie per la fiducia.
                </p>
                <button
                  onClick={reset}
                  className="mt-6 text-xs text-[#73706E] hover:text-[#3D3B3A] border-b border-[#73706E] pb-0.5"
                >
                  Chiudi
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
